if (!customElements.get('product-form')) {
  /*
    RELAXIM: 通信結果が確認できない場合の文言。成功も失敗も断定せず、
    重複追加を防ぐためにカート確認へ誘導する。
    このファイルが二重に読み込まれても再宣言でエラーにならないよう、定義はガードの内側に置く。
  */
  const RLX_UNKNOWN_RESULT_MESSAGE =
    '通信結果を確認できませんでした。重複追加を防ぐため、再操作する前にカートをご確認ください。';
  const RLX_CART_CHECK_LABEL = 'カートを確認する';
  /*
    RELAXIM: HTTP失敗時にShopify側の生レスポンス（description/message/errors）を
    画面・cartErrorイベント・consoleへそのまま出さない。購入者向けは固定文言、
    consoleは数値statusと項目の存在booleanだけに限定する。
  */
  const RLX_ADD_TO_CART_FAILURE_MESSAGE =
    'カートに追加できませんでした。商品内容と数量をご確認のうえ、もう一度お試しください。';

  /*
    RELAXIM: cart-notification / cart-drawer はページ内の全product-formで共有される。
    成功bodyの後着巻き戻りを防ぐため、cart/addの所有権もインスタンス単位ではなく
    このcustom-element定義スコープで1個だけ共有する。variant IDと停止理由以外の
    応答body・顧客情報・価格・数量は保持しない。
  */
  const RLX_CART_ADD_COORDINATOR = {
    owner: null,
    sequence: 0,
    activeToken: 0,
    variantLockReasons: new Map(),
    lockAllReason: null,
  };

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.variantIdInput = this.form ? this.form.querySelector('[name=id]') : null;

        /*
          RELAXIM: variant切替世代と、結果不明／売り切れ確定になったvariant IDを
          markup欠落による早期returnより前に初期化する。結果不明は同じvariantの
          重複追加だけを止め、別variantの購入経路へ波及させない。
        */
        this.rlxActiveVariantGeneration = 0;
        this.rlxLockedVariantIds = new Set();
        this.rlxVariantLockReasons = new Map();
        this.rlxSubmissionLockAll = false;
        this.rlxSubmissionLockAllReason = null;
        this.rlxLatestCartAddRequestToken = 0;
        this.rlxCartAddBusy = false;
        this.rlxVariantServerPurchaseStates = new Map();
        this.submissionLocked = false;
        this.submissionBusy = false;

        /*
          RELAXIM: Dawn標準はhidden商品IDを初期化時に常に有効化するため、
          Liquidが売り切れ・数量上限で付けたdisabledを消してしまう。
          可視の購入ボタンと同じ確定状態へ揃え、販売可能時だけ送信対象にする。
          ボタンまたはフォームが欠けた不正な構造はfail-closedで扱う。
        */
        if (this.variantIdInput) {
          this.variantIdInput.disabled = !this.submitButton || this.submitButton.disabled;
        }
        if (!this.form || !this.submitButton || !this.variantIdInput) return;

        /* 初期Liquidのbutton／hidden整合を、busy解除時の最初のserver可否として保持する */
        this.rlxRememberVariantServerPurchaseState(
          this.variantIdInput.value,
          !this.submitButton.disabled && !this.variantIdInput.disabled
        );

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        const cartCandidate = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        /*
          RELAXIM: custom elementが未upgrade、または一部メソッドだけ欠けた状態でも
          cart/add前に例外で止めない。完全な補助interfaceだけをsection描画に使い、
          不完全ならShopify標準POST成功後にカートページへ進む。
        */
        this.cart =
          cartCandidate &&
          typeof cartCandidate.getSectionsToRender === 'function' &&
          typeof cartCandidate.setActiveElement === 'function' &&
          typeof cartCandidate.renderContents === 'function'
            ? cartCandidate
            : null;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        /*
          後から生成されたquick-add等も、共有busyだけでなく同一variantの
          結果不明／売り切れ理由まで初期表示から3購入経路へ合成する。
        */
        this.rlxApplyCurrentSubmissionState();
      }

      /*
        RELAXIM: variant IDをSetの安定キーへ正規化する。ID欠落だけは安全側で
        全variantロックへ倒すため、nullを返して呼び出し側で区別する。
      */
      rlxVariantLockKey(variantId) {
        if (variantId === null || variantId === undefined || String(variantId) === '') return null;
        return String(variantId);
      }

      rlxCurrentVariantId() {
        return this.variantIdInput ? this.variantIdInput.value : null;
      }

      rlxIsCartAddBusy() {
        return RLX_CART_ADD_COORDINATOR.owner !== null;
      }

      rlxCartAddForms() {
        const forms =
          typeof document !== 'undefined' && typeof document.querySelectorAll === 'function'
            ? Array.from(document.querySelectorAll('product-form'))
            : [];
        if (RLX_CART_ADD_COORDINATOR.owner && !forms.includes(RLX_CART_ADD_COORDINATOR.owner)) {
          forms.push(RLX_CART_ADD_COORDINATOR.owner);
        }
        if (!forms.includes(this)) forms.push(this);
        return forms.filter(
          (form, index, all) =>
            form &&
            all.indexOf(form) === index &&
            typeof form.rlxApplyCartAddBusy === 'function' &&
            typeof form.rlxFinishCartAddBusy === 'function'
        );
      }

      rlxApplyGlobalCartAddBusy() {
        this.rlxCartAddForms().forEach((productForm) => {
          productForm.rlxCartAddBusy = true;
          productForm.rlxApplyCartAddBusy();
        });
      }

      rlxTryStartGlobalCartAdd() {
        if (RLX_CART_ADD_COORDINATOR.owner !== null) {
          this.rlxApplyGlobalCartAddBusy();
          return null;
        }
        const requestToken = RLX_CART_ADD_COORDINATOR.sequence + 1;
        RLX_CART_ADD_COORDINATOR.sequence = requestToken;
        RLX_CART_ADD_COORDINATOR.activeToken = requestToken;
        RLX_CART_ADD_COORDINATOR.owner = this;
        this.rlxLatestCartAddRequestToken = requestToken;
        this.rlxApplyGlobalCartAddBusy();
        return requestToken;
      }

      rlxIsGlobalCartAddOwner(requestToken) {
        return (
          RLX_CART_ADD_COORDINATOR.owner === this &&
          RLX_CART_ADD_COORDINATOR.activeToken === requestToken
        );
      }

      rlxIsLatestGlobalCartAddToken(requestToken) {
        return RLX_CART_ADD_COORDINATOR.sequence === requestToken;
      }

      rlxFinishGlobalCartAdd(requestToken) {
        if (!this.rlxIsGlobalCartAddOwner(requestToken)) return false;
        const productForms = this.rlxCartAddForms();
        RLX_CART_ADD_COORDINATOR.owner = null;
        RLX_CART_ADD_COORDINATOR.activeToken = 0;
        productForms.forEach((productForm) => productForm.rlxFinishCartAddBusy());
        return true;
      }

      /*
        RELAXIM: busy解除後に推測で購入可能へ戻さないため、最新の商品HTMLまたは
        初期Liquidから確認できたvariant別のserver購入可否だけを保持する。
      */
      rlxRememberVariantServerPurchaseState(variantId, purchasable) {
        const key = this.rlxVariantLockKey(variantId);
        if (key === null) return false;
        if (
          !this.rlxVariantServerPurchaseStates ||
          typeof this.rlxVariantServerPurchaseStates.set !== 'function'
        ) {
          this.rlxVariantServerPurchaseStates = new Map();
        }
        this.rlxVariantServerPurchaseStates.set(key, purchasable === true);
        return true;
      }

      rlxForgetVariantServerPurchaseState(variantId) {
        const key = this.rlxVariantLockKey(variantId);
        if (
          key === null ||
          !this.rlxVariantServerPurchaseStates ||
          typeof this.rlxVariantServerPurchaseStates.delete !== 'function'
        ) {
          return false;
        }
        return this.rlxVariantServerPurchaseStates.delete(key);
      }

      rlxGetVariantServerPurchaseState(variantId) {
        const key = this.rlxVariantLockKey(variantId);
        if (
          key === null ||
          !this.rlxVariantServerPurchaseStates ||
          typeof this.rlxVariantServerPurchaseStates.has !== 'function' ||
          !this.rlxVariantServerPurchaseStates.has(key)
        ) {
          return null;
        }
        return this.rlxVariantServerPurchaseStates.get(key) === true;
      }

      rlxSetDynamicCheckoutBlocked(blocked) {
        const wrapper = this.form ? this.form.querySelector('[data-rlx-dynamic-checkout]') : null;
        if (!wrapper) return;
        wrapper.hidden = blocked;
        wrapper.setAttribute('aria-hidden', blocked ? 'true' : 'false');
      }

      /* cart/addが未settledの間は、variant切替後も全購入経路を一時停止する */
      rlxApplyCartAddBusy() {
        this.submissionBusy = this.rlxIsCartAddBusy();
        this.rlxCartAddBusy = this.submissionBusy;
        if (!this.submissionBusy) return false;
        const isOwner = RLX_CART_ADD_COORDINATOR.owner === this;
        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (isOwner) {
            this.submitButton.classList.add('loading');
          } else {
            this.submitButton.classList.remove('loading');
          }
        }
        if (this.variantIdInput) this.variantIdInput.disabled = true;
        this.rlxSetDynamicCheckoutBlocked(true);
        this.rlxToggleSpinner(!isOwner);
        return true;
      }

      rlxApplyCurrentSubmissionState() {
        if (this.rlxApplyCurrentSubmissionLock()) return true;
        if (this.rlxApplyCartAddBusy()) return true;

        const purchasable = this.rlxGetVariantServerPurchaseState(this.rlxCurrentVariantId()) === true;
        if (this.submitButton) {
          this.submitButton.disabled = !purchasable;
          if (purchasable) {
            this.submitButton.removeAttribute('aria-disabled');
          } else {
            this.submitButton.setAttribute('aria-disabled', 'true');
          }
        }
        if (this.variantIdInput) this.variantIdInput.disabled = !purchasable;
        this.rlxSetDynamicCheckoutBlocked(!purchasable);
        return !purchasable;
      }

      /*
        RELAXIM: busy解除後は現在variantの恒久理由を最優先し、理由が無ければ
        最新server可否が明示的にtrueの時だけ3購入経路を復元する。
      */
      rlxFinishCartAddBusy() {
        this.rlxCartAddBusy = false;
        this.submissionBusy = false;
        if (this.submitButton) this.submitButton.classList.remove('loading');
        this.rlxToggleSpinner(true);
        return !this.rlxApplyCurrentSubmissionState();
      }

      rlxNormalizeSubmissionLockReason(reason) {
        return reason === 'soldout' ? 'soldout' : 'unknown';
      }

      /*
        RELAXIM: lock有無と理由の照会は副作用を持たせない。ページ共有Mapを正本にし、
        同じvariant IDの別フォームも停止する。インスタンス側Set／Mapは既存互換のミラー。
      */
      rlxGetSubmissionLockReason(variantId) {
        if (RLX_CART_ADD_COORDINATOR.lockAllReason) {
          return RLX_CART_ADD_COORDINATOR.lockAllReason;
        }
        if (this.rlxSubmissionLockAll === true) {
          return this.rlxSubmissionLockAllReason || 'unknown';
        }
        const key = this.rlxVariantLockKey(variantId);
        if (key === null) return null;
        if (
          RLX_CART_ADD_COORDINATOR.variantLockReasons &&
          RLX_CART_ADD_COORDINATOR.variantLockReasons.has(key)
        ) {
          return RLX_CART_ADD_COORDINATOR.variantLockReasons.get(key);
        }
        if (
          this.rlxVariantLockReasons &&
          typeof this.rlxVariantLockReasons.get === 'function' &&
          this.rlxVariantLockReasons.has(key)
        ) {
          return this.rlxVariantLockReasons.get(key);
        }
        if (
          this.rlxLockedVariantIds &&
          typeof this.rlxLockedVariantIds.has === 'function' &&
          this.rlxLockedVariantIds.has(key)
        ) {
          return 'unknown';
        }
        return null;
      }

      rlxIsSubmissionLockedForVariant(variantId) {
        return Boolean(this.rlxGetSubmissionLockReason(variantId));
      }

      /*
        RELAXIM: requested variantのlockをページ共有Mapへ記録し、呼出元のSet／Mapへも
        互換ミラーするhelper。ID欠落時は理由をvariantへ帰属できないため全体unknownへ倒す。
        同一IDでunknownを一度でも記録した後は、後着soldoutでカート確認導線を消さない。
      */
      rlxRecordSubmissionLock(variantId, reason = 'unknown') {
        const key = this.rlxVariantLockKey(variantId);
        const normalizedReason = this.rlxNormalizeSubmissionLockReason(reason);
        if (key === null) {
          RLX_CART_ADD_COORDINATOR.lockAllReason = 'unknown';
          this.rlxSubmissionLockAll = true;
          this.rlxSubmissionLockAllReason = 'unknown';
          return this.rlxSubmissionLockAllReason;
        }

        if (!this.rlxLockedVariantIds || typeof this.rlxLockedVariantIds.add !== 'function') {
          this.rlxLockedVariantIds = new Set();
        }
        if (!this.rlxVariantLockReasons || typeof this.rlxVariantLockReasons.set !== 'function') {
          this.rlxVariantLockReasons = new Map();
        }
        this.rlxLockedVariantIds.add(key);
        const sharedExistingReason = RLX_CART_ADD_COORDINATOR.variantLockReasons.get(key);
        if (sharedExistingReason !== 'unknown') {
          RLX_CART_ADD_COORDINATOR.variantLockReasons.set(key, normalizedReason);
        }
        const sharedReason = RLX_CART_ADD_COORDINATOR.variantLockReasons.get(key);
        this.rlxVariantLockReasons.set(key, sharedReason);
        return sharedReason;
      }

      /*
        RELAXIM: 現在variantに既に記録されたlockだけを購入UIへ反映する。
        記録は一切増やさないため、A lock済みの状態でBのonSubmit／finallyが
        このhelperを呼んでもBをSetへ追加しない。
      */
      rlxApplyCurrentSubmissionLock() {
        const currentVariantId = this.rlxCurrentVariantId();
        const reason = this.rlxGetSubmissionLockReason(currentVariantId);
        this.submissionLocked = Boolean(reason);
        if (!reason) return false;

        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
        if (this.variantIdInput) this.variantIdInput.disabled = true;
        this.rlxSetDynamicCheckoutBlocked(true);

        if (reason === 'soldout') {
          if (this.submitButton) this.rlxSetSoldOutLabelState(true);
          this.rlxToggleCartCheckLink(false);
        } else {
          if (this.submitButton) this.rlxSetSoldOutLabelState(false);
          this.handleErrorMessage(RLX_UNKNOWN_RESULT_MESSAGE, { critical: true });
        }
        return true;
      }

      /*
        RELAXIM: 記録と現在UI反映を明示的に分離する。requested IDが現在IDと異なる
        場合はAの記録だけを残し、Bのbutton／hidden／表示へ触れない。
      */
      lockSubmission(variantId = this.rlxCurrentVariantId(), reason = 'unknown') {
        const requestedKey = this.rlxVariantLockKey(variantId);
        this.rlxRecordSubmissionLock(variantId, reason);
        if (requestedKey === null) {
          this.rlxCartAddForms().forEach((productForm) => productForm.rlxApplyCurrentSubmissionLock());
          return true;
        }
        const currentKey = this.rlxVariantLockKey(this.rlxCurrentVariantId());
        if (requestedKey !== null && requestedKey !== currentKey && this.rlxSubmissionLockAll !== true) {
          return false;
        }
        return this.rlxApplyCurrentSubmissionLock();
      }

      /*
        RELAXIM: 在庫切れ確定と判定できる場合だけ、売り切れ表示・恒久ロックへ進む。
        Shopifyの/cart/add.jsは数量上限・購入頻度（販売プラン）不一致・入力値エラー等でも
        同じ422形状で返るため、HTTPステータスだけでは在庫切れと断定できない。
        本文のmessage/description/errorsに在庫由来の語が明示されている場合だけ確定扱いにする。
        非在庫の4xxはエラー文を見せて再送信を許可する。HTTP 5xxはShopify側の追加処理が
        済んだ後に応答だけ失敗した可能性を否定できないため、結果不明として再送信を止める。
      */
      rlxIsConfirmedSoldOut(status, response) {
        if (status !== 422 || !response) return false;
        const texts = [response.message, response.description];
        const errors = response.errors;
        if (Array.isArray(errors)) {
          texts.push(...errors);
        } else if (errors && typeof errors === 'object') {
          Object.keys(errors).forEach((key) => {
            const value = errors[key];
            if (Array.isArray(value)) {
              texts.push(...value);
            } else {
              texts.push(key, value);
            }
          });
        } else if (errors) {
          texts.push(String(errors));
        }
        const patternA = /\bis (?:already )?sold[ -]?out\b(?:[.!\n]|$)/i;
        const patternB = /(?:すでに|既に)[^。.!?\n]{0,120}(?:売り切れ|売切れ|品切れ|在庫切れ)(?:です|となっています|になっています)?(?:[。.!！\n]|$)/;
        const patternC = /(?:^|[。.!?\n])[^。.!?\n]{0,120}(?:売り切れ|売切れ|品切れ|在庫切れ)(?:です|となっています|になっています)(?:[。.!！\n]|$)/;
        return texts.map((text) => String(text).trim()).filter(Boolean).some((text) =>
          patternA.test(text) || patternB.test(text) || patternC.test(text)
        );
      }

      /*
        RELAXIM: 在庫切れ確定と、それ以外の失敗（結果不明含む）とで、
        通常ラベル／sold-out表示の可視状態を明示的に切り替える。
        呼び出し側の分岐漏れで両方とも表示・非表示になる事故を防ぐため、
        必ずこのメソッド経由で切り替える。
      */
      rlxSetSoldOutLabelState(soldOut) {
        const submitLabel = this.submitButton.querySelector('span');
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (submitLabel) {
          if (soldOut) {
            submitLabel.classList.add('hidden');
          } else {
            submitLabel.classList.remove('hidden');
          }
        }
        if (soldOutMessage) {
          if (soldOut) {
            soldOutMessage.classList.remove('hidden');
          } else {
            soldOutMessage.classList.add('hidden');
          }
        }
      }

      /*
        RELAXIM: variant-selects側（global.js）が、variant切替の開始直後
        （currentVariant有無の判定より前）に呼び出す専用メソッド。
        有効/無効/売り切れいずれの切替でも世代を進める。
        不存在の組み合わせ（hidden商品IDのvalueが変化しないケース）でも、
        この世代番号だけが「variantが切り替わり始めた」ことを示す唯一の合図になる。
      */
      rlxNotifyVariantSelectionChanging() {
        this.rlxActiveVariantGeneration = (this.rlxActiveVariantGeneration || 0) + 1;
        /* 切替先の最新商品HTMLが届くまで、旧variantのserver可否を流用しない */
        if (
          this.rlxVariantServerPurchaseStates &&
          typeof this.rlxVariantServerPurchaseStates.clear === 'function'
        ) {
          this.rlxVariantServerPurchaseStates.clear();
        }
      }

      /*
        RELAXIM: cart/addの応答が、送信時点のvariant選択にとって今も「現在の応答」と
        言えるかを判定する。世代（rlxNotifyVariantSelectionChanging経由）・
        hidden商品IDの値が変わるか一時busyが既に終わっていれば、この応答は古いとみなす。
        busy自身がbutton／hiddenをdisabledにするため、native disabledはstale判定に使わない。
        在庫切れ確定ロックの適用と、非在庫失敗時のラベル復元の両方で使う。
        現在の売切れ・無効組合せ・数量不可・別variantの状態を、
        古いHTTP500・非在庫422で上書きしないためのガード。
      */
      rlxIsResponseCurrent(requestGeneration, requestedVariantId, requestToken) {
        if (requestGeneration !== this.rlxActiveVariantGeneration) return false;
        if (!this.variantIdInput) return false;
        if (String(this.variantIdInput.value) !== String(requestedVariantId)) return false;
        return this.rlxIsGlobalCartAddOwner(requestToken);
      }

      /*
        RELAXIM: finally()でaria-disabledを解除してよいかどうかの判定専用。
        送信したvariantと一致するかは問わず、今画面に見えている状態だけで判断する。
        売切れ・無効組合せ・数量不可のいずれかでnative disabledになっていればfalseにする。
        古い応答の到着有無に関わらず、現在購入可能な別variantを永久ロックしないためのもの。
      */
      rlxIsCurrentlyPurchasable() {
        return Boolean(this.variantIdInput) && !this.variantIdInput.disabled && !this.submitButton.disabled;
      }

      /* RELAXIM: routes は theme.liquid が定義する。取得できない場合も空リンクにしない */
      rlxCartUrl() {
        if (typeof routes !== 'undefined' && routes && routes.cart_url) return routes.cart_url;
        if (typeof window !== 'undefined' && window.routes && window.routes.cart_url) {
          return window.routes.cart_url;
        }
        return '/cart';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        /*
          RELAXIM: 結果不明・在庫切れ確定のあとは、同じページを再読み込みするまで再送信しない。
          外部スクリプトがdisabledを外した状況でも、ここで安全状態へ戻してから抜ける。
        */
        if (this.rlxApplyCurrentSubmissionState()) return;
        /*
          RELAXIM: クリック不能な売り切れボタンでも、Enter・外部スクリプト等から
          submitイベント自体は発火できる。native disabled、hidden商品IDの欠落・
          disabled、処理中aria-disabledのいずれかならcart/addへ通信しない。
        */
        if (
          this.submitButton.disabled ||
          !this.variantIdInput ||
          this.variantIdInput.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        )
          return;

        this.handleErrorMessage();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        /*
          RELAXIM: 送信時点のvariant世代とhidden商品IDを保持する。
          応答到着時点でこれらが変わっていれば、cart/add POST保留中に
          variantが切り替わったとみなし、古い応答で現在の画面状態を上書きしない。
        */
        const requestGeneration = this.rlxActiveVariantGeneration || 0;
        const requestedVariantId = formData.get('id');

        /*
          FormDataへIDを確定した直後、fetch開始前に同期的にページ内全フォームbusyへ移る。
          hiddenを先にdisabledにするとFormDataからIDが落ちるため、この順序を固定する。
        */
        if (this.rlxVariantLockKey(requestedVariantId) === null) {
          this.lockSubmission(null, 'unknown');
          return;
        }
        this.rlxRememberVariantServerPurchaseState(requestedVariantId, true);
        const submitRequestToken = this.rlxTryStartGlobalCartAdd();
        if (submitRequestToken === null) return;

        /*
          RELAXIM: 生のHTTP状態をJSON本文の status と分離して保持する。
          HTTP 422で本文に status が無い場合でも成功扱いしないための判定材料にする。
        */
        let httpResult = null;
        /* HTTP成功JSONが確定した場合だけ、空カート表示を非空状態へ進める */
        let addSucceeded = false;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            httpResult = { ok: response.ok, status: response.status };
            return response.json();
          })
          .then((response) => {
            const httpFailed = !httpResult || httpResult.ok === false;
            if (httpFailed || response.status) {
              const status = response.status || (httpResult ? httpResult.status : undefined);
              const logStatus = typeof status === 'number' && Number.isFinite(status) ? status : undefined;
              const httpStatus = httpResult ? httpResult.status : undefined;
              const unknownHttpResult =
                !httpResult ||
                (httpResult.ok === false &&
                  (!Number.isInteger(httpStatus) || httpStatus >= 500));
              const responseIsCurrent = this.rlxIsResponseCurrent(
                requestGeneration,
                requestedVariantId,
                submitRequestToken
              );
              const confirmedSoldOut = this.rlxIsConfirmedSoldOut(status, response);

              /*
                RELAXIM: 応答項目の生値はconsole・DOM・cartErrorのどこにも出さない。
                調査用ログはHTTP状態と項目の存在有無だけに限定し、顧客情報・注文情報・
                周辺アプリの内部情報が応答へ混入してもブラウザログへ残さない。
              */
              const warningDetails = {
                status: logStatus,
                hasErrors: response.errors !== undefined && response.errors !== null,
                hasDescription: response.description !== undefined && response.description !== null,
                hasMessage: response.message !== undefined && response.message !== null,
              };

              if (unknownHttpResult) {
                /*
                  RELAXIM: JSONを読めたHTTP 5xxも結果不明。requested variantへunknownを
                  記録して再POSTを止める。現在が別variantなら記録だけ残し、現在UIへは触れない。
                */
                const requestedVariantKey = this.rlxVariantLockKey(requestedVariantId);
                const currentVariantKey = this.rlxVariantLockKey(this.rlxCurrentVariantId());
                const affectsCurrentVariant =
                  requestedVariantKey === null || requestedVariantKey === currentVariantKey;
                console.warn('[RELAXIM] cart/add failed', warningDetails);
                this.lockSubmission(requestedVariantId, 'unknown');
                if (!affectsCurrentVariant) return;
                console.error('[RELAXIM] cart/addの通信結果を確認できませんでした。');
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: RLX_UNKNOWN_RESULT_MESSAGE,
                  message: RLX_UNKNOWN_RESULT_MESSAGE,
                });
                return;
              }

              /*
                RELAXIM: staleな既知4xx失敗はsoldoutを含め完全無副作用にする。
                A→B→A後の新A成功より古いsoldoutを後着正本にしない。
              */
              if (!responseIsCurrent) return;

              console.warn('[RELAXIM] cart/add failed', warningDetails);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: RLX_ADD_TO_CART_FAILURE_MESSAGE,
                message: RLX_ADD_TO_CART_FAILURE_MESSAGE,
              });
              this.handleErrorMessage(RLX_ADD_TO_CART_FAILURE_MESSAGE);

              if (confirmedSoldOut) {
                /* 在庫切れ確定のみ、表示要素の有無に関係なく送信禁止を恒久化する */
                this.lockSubmission(requestedVariantId, 'soldout');
              } else {
                /*
                  RELAXIM: 在庫切れと断定できない4xxは成功扱いにはしないが恒久ロックはしない。
                  button／hidden／高速購入の復元はfinallyでbusy解除後、
                  最新server可否を正本にして行い、数量不可等をここで上書きしない。
                */
                this.rlxSetSoldOutLabelState(false);
              }
              /*
                RELAXIM: 送信後にvariantが切り替わっていた場合（世代不一致・
                hidden商品IDの変更/disabled・購入ボタンのnative disabledのいずれか）、
                この応答は現在の選択についてのものではない。現在の売切れ・無効組合せ・
                数量不可・別variantの表示状態を、古いHTTP500・非在庫422で上書きしない。
              */
              return;
            }

            addSucceeded = true;

            if (!this.cart) {
              window.location = this.rlxCartUrl();
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (!this.rlxIsLatestGlobalCartAddToken(submitRequestToken)) return;
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch(() => {
            /*
              RELAXIM: fetch拒否・response.json()拒否は「結果不明」。
              cart/add がShopify側で確定している可能性があるため、成功も失敗も断定せず、
              requested variant IDを必ずlock Setへ記録して自動リトライを止める。
              現在が別variantならその購入UIには触れず、同じvariantへ戻った時だけ停止する。
            */
            const requestedVariantKey = this.rlxVariantLockKey(requestedVariantId);
            const currentVariantKey = this.rlxVariantLockKey(this.rlxCurrentVariantId());
            const affectsCurrentVariant = requestedVariantKey === null || requestedVariantKey === currentVariantKey;
            this.lockSubmission(requestedVariantId, 'unknown');
            if (!affectsCurrentVariant) return;
            console.error('[RELAXIM] cart/addの通信結果を確認できませんでした。');
          })
          .finally(() => {
            if (
              addSucceeded &&
              this.cart &&
              this.cart.classList &&
              typeof this.cart.classList.contains === 'function' &&
              this.cart.classList.contains('is-empty')
            )
              this.cart.classList.remove('is-empty');

            /*
              RELAXIM: coordinatorのownerとactive tokenが一致するPromiseだけが
              ページ共有busyを解除し、全product-formを各自のserver可否へ復元する。
            */
            this.rlxFinishGlobalCartAdd(submitRequestToken);
          });
      }

      /* RELAXIM: spinnerが無いmarkupでも例外を出さない */
      rlxToggleSpinner(hidden) {
        const spinner = this.querySelector('.loading__spinner');
        if (!spinner) return;
        if (hidden) spinner.classList.add('hidden');
        else spinner.classList.remove('hidden');
      }

      /*
        RELAXIM: クイック追加のように初期markupへエラー領域が無い場合でも黙って抜けない。
        role="alert" の領域を安全に生成し、警告を必ず可視化する。
      */
      rlxEnsureErrorMessageWrapper() {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (this.errorMessageWrapper) return this.errorMessageWrapper;
        if (typeof document === 'undefined' || typeof document.createElement !== 'function') return null;

        const wrapper = document.createElement('div');
        wrapper.className = 'product-form__error-message-wrapper';
        wrapper.setAttribute('role', 'alert');
        wrapper.hidden = true;
        const message = document.createElement('span');
        message.className = 'product-form__error-message';
        wrapper.appendChild(message);
        if (typeof this.prepend === 'function') this.prepend(wrapper);
        else this.appendChild(wrapper);

        this.errorMessageWrapper = wrapper;
        return wrapper;
      }

      /*
        RELAXIM: ヘッダーのカートだけに依存させず、エラー領域からカート確認へ進めるようにする。
      */
      rlxToggleCartCheckLink(show) {
        if (!this.errorMessageWrapper) return;
        let link = this.errorMessageWrapper.querySelector('.rlx-cart-check-link');
        if (!link) {
          if (!show) return;
          if (typeof document === 'undefined' || typeof document.createElement !== 'function') return;
          link = document.createElement('a');
          link.className = 'rlx-cart-check-link';
          link.setAttribute('href', this.rlxCartUrl());
          link.textContent = RLX_CART_CHECK_LABEL;
          this.errorMessageWrapper.appendChild(link);
        }
        link.hidden = !show;
      }

      handleErrorMessage(errorMessage = false, options = {}) {
        /*
          RELAXIM: 通常のエラー表示は data-hide-errors を尊重する。
          結果不明の警告は重複購入の防止に必要なため critical として必ず表示する。
        */
        if (this.hideErrors && !options.critical) return;

        if (options.critical) this.rlxEnsureErrorMessageWrapper();
        else
          this.errorMessageWrapper =
            this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.errorMessageWrapper.hidden = !errorMessage;

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }

        this.rlxToggleCartCheckLink(Boolean(errorMessage) && Boolean(options.critical));
      }
    }
  );
}
